import {rangeNumber} from '@coolcolduk/util';
import {useEffect, useState} from 'react';

export interface UseDataNavigationOptions<T> {
  /** function to find the initial index of the data */
  initialIndexFinder?: (data: T[]) => number;
  /** function to run after clicking end button. currentData is before navigation */
  handleEnd?: (currentData: T) => void;
  /** function to run after clicking next button. currentData is before navigation, nextData is after navigation */
  handleNext?: (currentData: T, nextData: T) => void;
  /** function to run after clicking previous button. currentData is before navigation, previousData is after navigation */
  handlePrevious?: (currentData: T, previousData: T) => void;
  /** function to run after clicking goto button. currentData is before navigation, nextData is after navigation */
  handleGoto?: (currentData: T, nextData: T) => void;
}

/**
 * A hook to navigate through a list of data
 * @param data - The list of data to navigate through. use empty array if the data is not yet available.
 * @param initialIndexFinder - A function to find the initial index of the data
 * @returns An object with the current data, current index, next function, and previous function
 */
export default function useDataNavigation<T>(data: T[], options: UseDataNavigationOptions<T> = {}) {
  // -1 means the data is not yet available
  const [currentIndex, setCurrentIndex] = useState(-1);

  useEffect(() => {
    if (data.length === 0) {
      setCurrentIndex(-1);
      return;
    }

    if (options.initialIndexFinder) {
      setCurrentIndex(rangeNumber(options.initialIndexFinder(data), 0, data.length - 1));
      return;
    }
    setCurrentIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);

  const next = () => {
    // do nothing when no data available
    if (data.length === 0) return;

    const nextIndex = currentIndex + 1;

    // if already at last question, run handleEndQuestion
    if (nextIndex >= data.length) {
      if (options.handleEnd) {
        options.handleEnd(data[currentIndex]);
      }
      return;
    }

    setCurrentIndex(rangeNumber(nextIndex, 0, data.length - 1));
    if (options.handleNext) {
      options.handleNext(data[currentIndex], data[nextIndex]);
    }
  };

  const previous = () => {
    if (currentIndex - 1 < 0) return;

    const previousIndex = currentIndex - 1;

    setCurrentIndex(rangeNumber(previousIndex, 0, data.length - 1));
    if (options.handlePrevious) {
      options.handlePrevious(data[currentIndex], data[previousIndex]);
    }
  };

  const goto = (index: number) => {
    const currentData = data[currentIndex];
    const gotoIndex = rangeNumber(index, 0, data.length - 1);
    setCurrentIndex(gotoIndex);
    if (options.handleGoto) {
      options.handleGoto(currentData, data[gotoIndex]);
    }
  };

  return {
    currentData: data[currentIndex] || undefined,
    currentIndex,
    next,
    previous,
    goto,
    isFirst: currentIndex === 0,
    isLast: currentIndex === data.length - 1,
  };
}
